import React, { useState } from 'react';
import {
  Box, Button, TextField, Typography, Container, Alert,
  ThemeProvider, CssBaseline, Stack, Card, FormControlLabel, Checkbox, Link as MuiLink,
} from '@mui/material';
import { Restaurant, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { landingTheme } from '../theme/landingTheme';
import { trackSignupSuccess } from '../gtag';

export default function Signup() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!agreeTerms) {
      setError('Please agree to the Terms & Conditions and Privacy Policy to continue');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Signup failed');

      const loginRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!loginRes.ok) throw new Error('Account created. Please sign in from the home page.');
      const loginData = await loginRes.json();
      localStorage.setItem('token', loginData.token);
      trackSignupSuccess(email);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={landingTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(145deg, #312e81 0%, #4f46e5 50%, #6366f1 100%)', display: 'flex', alignItems: 'center', py: 6 }}>
        <Container maxWidth="sm">
          <Stack alignItems="center" spacing={2} sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              <img src="/logo192.png" alt="HomeBillingPOS Logo" style={{ height: 36, width: 36, objectFit: 'contain' }} />
            </Box>
            <Typography variant="h4" fontWeight={800} color="#fff">Create owner account</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 400 }}>
              7 days free on your first desktop — no card needed. Main branch included; go straight to your dashboard.
            </Typography>
          </Stack>
          <Card sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <TextField label="Restaurant / owner name" fullWidth value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />
                <TextField label="Email" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} required />
                <TextField label="Password" type="password" fullWidth value={password} onChange={(e) => setPassword(e.target.value)} required inputProps={{ minLength: 6 }} helperText="Minimum 6 characters" />
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={
                    <Checkbox
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      size="small"
                      sx={{ pt: 0.25 }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                      I agree to the{' '}
                      <MuiLink href="/terms" target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
                        Terms &amp; Conditions
                      </MuiLink>
                      {' '}and{' '}
                      <MuiLink href="/privacy" target="_blank" rel="noopener noreferrer" underline="hover" fontWeight={600}>
                        Privacy Policy
                      </MuiLink>
                    </Typography>
                  }
                />
                <Button type="submit" variant="contained" size="large" fullWidth disabled={loading || !agreeTerms} endIcon={<ArrowForward />}>
                  {loading ? 'Creating account...' : 'Start 7-day free trial'}
                </Button>
                <Button fullWidth color="inherit" onClick={() => navigate('/')}>Back to home</Button>
              </Stack>
            </form>
          </Card>
        </Container>
      </Box>
    </ThemeProvider>
  );
}